import { useState, useEffect } from 'react';
import { useLocaleStore } from '@/lib/core/stores';
import { getContentById } from '@/services/platform/data/cms';
import { ImagesToImageMap } from '@/lib/core/utils/image';
import type { IApiContentResponseModel, ImageWithCrops } from '@/services/platform/api/model';

export interface CategoryCardItem {
  id: string;
  image?: string;
  imageUrl?: string;
  title?: string;
  description?: string;
  contentType?: string;
}

type WithProperties = {
  properties?: { image?: ImageWithCrops[] | null; title?: string; description?: string; imageUrl?: string };
};

function extractItem(raw: IApiContentResponseModel, id: string): CategoryCardItem {
  const props = (raw as unknown as WithProperties).properties;
  return {
    id,
    image: ImagesToImageMap(props?.image)?.src,
    imageUrl: props?.imageUrl,
    // Editors sometimes publish without filling Title — fall back to the node name.
    title: props?.title || raw.name || undefined,
    description: props?.description,
    contentType: raw.contentType,
  };
}

export function useShopCategoryPickerItems(picker?: IApiContentResponseModel[] | null): {
  items: CategoryCardItem[];
  loading: boolean;
} {
  const locale = useLocaleStore(s => s.locale);
  const [items, setItems] = useState<CategoryCardItem[]>([]);
  const [loading, setLoading] = useState(false);

  const ids = (picker ?? []).map(p => p.id).filter(Boolean) as string[];
  const idsKey = ids.join(',');

  useEffect(() => {
    if (ids.length === 0) {
      setItems([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    Promise.all(ids.map(id => getContentById(id, locale)))
      .then(results => {
        if (cancelled) return;
        setItems(
          results
            .map((raw, i) => (raw ? extractItem(raw, ids[i]) : null))
            .filter(Boolean) as CategoryCardItem[]
        );
      })
      .catch(() => {
        if (!cancelled) setItems([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [idsKey, locale]);

  return { items, loading };
}
